import { CUSTOM_TERMINALS } from '../constants/terminalConstants';
import { SEZIONI, SEZIONI_AWG } from '../constants/cableConstants';
import { TERMINAL_DATA } from '../constants/terminalConstants';
import { universalSort, createSortContext } from './sortingLogic';
import { COLOR_MAP } from '../constants/colorConstants';
import { normalizeColorToCanonical } from './colorUtils';
import { getGroupTitle } from './groups';
import { translations } from '../i18n/translations';
import { withEW3035Settings } from '../machines/EW-3035+L';
import { getEffectiveSectionValue, normalizeSectionForComparison } from './sectionNormalization';
import { sanitizeTerminalData } from './terminalDataNormalization';

const parseCache = new Map();
// canTerminalHandleMultipleSingles ora legge allowsMultiple da TERMINAL_DATA
const canTerminalHandleMultipleSingles = (sezione, terminal, terminali = TERMINAL_DATA) => {
  const config = (terminali || TERMINAL_DATA)[terminal] || {};
  return Boolean(terminal) && config.allowsMultiple === true;
};

export const parseSignature = (sig) => {
  if (parseCache.has(sig)) {
    return parseCache.get(sig);
  }

  const str = String(sig);

  // Gestione pattern con trattino - cattura TUTTO dopo il trattino
  const dashPattern = /^([A-Za-z]*\d*(?:\.\d+)?)-(.+)$/;
  const dashMatch = str.match(dashPattern);

  if (dashMatch) {
    const result = {
      letter: '',
      integer: 0,
      decimal: 0,
      original: sig,
      hasPattern: true,
      patternBase: dashMatch[1],
      patternSuffix: dashMatch[2]
    };

    parseCache.set(sig, result);
    return result;
  }

  // Logica esistente per pattern normali
  const letterMatch = str.match(/^([A-Za-z]*)/);
  const integerMatch = str.match(/([0-9]+)/);
  const decimalMatch = str.match(/\.([0-9]+)/);

  const result = {
    letter: letterMatch ? letterMatch[1] : '',
    integer: integerMatch ? parseInt(integerMatch[1]) : 0,
    decimal: decimalMatch ? parseInt(decimalMatch[1]) : 0,
    original: sig,
    hasPattern: false,
    patternBase: '',
    patternSuffix: ''
  };

  parseCache.set(sig, result);
  return result;
};

// Funzioni di conversione
export const convertMetricToAWG = (metricValue) => {
  if (metricValue == null) return metricValue;
  const numValue = parseFloat(metricValue);
  if (isNaN(numValue)) return metricValue;

  const exactSection = SEZIONI_AWG.find(s => s.metric === numValue);
  if (exactSection) return exactSection.val;

  // Se non c'è una corrispondenza esatta (es. per somma di cavi), trova l'AWG più vicino
  let closest = null;
  let minDiff = Infinity;
  for (let s of SEZIONI_AWG) {
    if (typeof s.metric === 'number') {
      let diff = Math.abs(s.metric - numValue);
      // A parità di distanza prendiamo la sezione maggiore (AWG minore)
      if (diff < minDiff || (diff === minDiff && closest && s.metric > closest.metric)) {
        minDiff = diff;
        closest = s;
      }
    }
  }
  return closest ? closest.val : metricValue;
};

export const convertAWGToMetric = (awgValue) => {
  const awgSection = SEZIONI_AWG.find(s => s.val === awgValue);
  return awgSection ? awgSection.metric : awgValue;
};

// Funzione per normalizzare i colori per la visualizzazione
export const normalizeColorForVisualization = (color) => {
  return normalizeColorToCanonical(color);
};

export const getCableColorForSVG = (colorName) => {
  const normalized = normalizeColorForVisualization(colorName);
  return COLOR_MAP[normalized] || '#808080'; // Default grigio come nell'originale
};

// La sezione è sempre memorizzata in mm². Per la visualizzazione, se useAWG è
// attivo, convertiamo il valore metrico nel calibro AWG corrispondente.
export const formatSezione = (sezione, useAWG) => {
  const displayValue = useAWG ? convertMetricToAWG(sezione) : sezione;
  return useAWG ? `${displayValue} AWG` : `${displayValue}mm²`;
};

/**
 * Determina il separatore corretto per la denominazione di un cavo multipolare.
 * - "G" se il cavo ha almeno un polo di terra
 * - "x" minuscola se non ce ne sono
 *
 * Formato risultante: `${numeroPoli}G${sezione}` oppure `${numeroPoli}x${sezione}`
 *
 * @param {Array} poli - Array dei poli del cavo multipolare
 * @returns {string} Il separatore corretto: 'G' o 'x'
 */
export const getMultipolarSeparator = (poli) => {
  if (!Array.isArray(poli) || poli.length === 0) return 'x';
  const hasEarth = poli.some(p => normalizeColorToCanonical(p.colore) === 'YE/GN');
  return hasEarth ? 'G' : 'x';
};

/**
 * Restituisce la label completa per un cavo multipolare nel formato NxS o NGS.
 * Esempio: "3G1.5" (3 poli con terra, sezione 1.5) o "4x1.5" (4 poli senza terra)
 *
 * @param {Array} poli - Array dei poli del cavo multipolare
 * @param {boolean} useAWG - Se true, usa la sezione in formato AWG
 * @returns {string} La label nel formato corretto
 */
export const getMultipolarLabel = (poli, useAWG = false) => {
  if (!Array.isArray(poli) || poli.length === 0) return '';
  const count = poli.length;
  // poli[0].sezione è sempre in mm²: convertiamo in AWG solo per la label
  const metricSection = poli[0]?.sezione || 0.5;
  const section = useAWG ? convertMetricToAWG(metricSection) : metricSection;
  const separator = getMultipolarSeparator(poli);
  return `${count}${separator}${section}`;
};

export const getCableLabelTranslation = (language, key) => {
  const lang = translations[language] ? language : 'it';
  return translations[lang]?.cableLabels?.[key] || translations.it.cableLabels[key] || key;
};

export const getNonPrintableSiglature = () => {
  const labels = Object.values(translations).flatMap(language => [
    language?.cableLabels?.earthWire,
    language?.cableLabels?.noMarking
  ]);

  return [...new Set(labels.filter(Boolean))];
};

export const getSezioniArray = (useAWG) => {
  return useAWG ? SEZIONI_AWG : SEZIONI;
};

export const isStandardCable = (cable) => Boolean(cable && !(cable.poli && Array.isArray(cable.poli)));

export const normalizeStandardCable = (cable = {}) => {
  if (!isStandardCable(cable)) return cable;

  const legacySiglatura = String(cable.siglatura || '').trim();
  const siglaturaA = cable.siglaturaA !== undefined ? String(cable.siglaturaA || '') : legacySiglatura;
  const siglaturaB = cable.siglaturaB !== undefined ? String(cable.siglaturaB || '') : legacySiglatura;
  const useSeparateSiglature = cable.useSeparateSiglature === true && siglaturaA !== siglaturaB;

  const normalized = {
    ...cable,
    siglaturaA,
    siglaturaB,
    useSeparateSiglature
  };

  if ('siglatura' in normalized) {
    delete normalized.siglatura;
  }

  return normalized;
};

export const getStandardCableSideSiglature = (cable = {}) => {
  const normalized = normalizeStandardCable(cable);
  const siglaturaA = String(normalized.siglaturaA || '').trim();
  const siglaturaB = normalized.useSeparateSiglature
    ? String(normalized.siglaturaB || '').trim()
    : siglaturaA;

  return { siglaturaA, siglaturaB };
};

export const normalizeMultipolarPole = (polo = {}) => ({
  ...polo,
  siglaturaA: String(polo.siglaturaA || '').trim(),
  siglaturaB: String(polo.siglaturaB || '').trim()
});

export const getStandardCableDisplaySiglatura = (cable = {}) => {
  const { siglaturaA, siglaturaB } = getStandardCableSideSiglature(cable);

  if (siglaturaA && siglaturaB && siglaturaA !== siglaturaB) {
    return `${siglaturaA} -> ${siglaturaB}`;
  }

  return siglaturaA || siglaturaB || '';
};

export const getStandardCableReadableSiglatura = (cable = {}) => {
  const normalized = normalizeStandardCable(cable);
  const { siglaturaA, siglaturaB } = getStandardCableSideSiglature(normalized);

  if (normalized.useSeparateSiglature) {
    const displayA = siglaturaA || '_';
    const displayB = siglaturaB || '_';
    return `${displayA} <-> ${displayB}`;
  }

  return siglaturaA || siglaturaB || '';
};

// Rimuove i punti virtuali (·) usati per raggruppare cavi in multipolari senza mostrare nello schematico
export const stripVirtualDots = (text) => {
  if (!text) return text;
  // Rimuove tutti i caratteri · (middle dot) dal testo
  return String(text).replace(/·/g, '').trim();
};

export const preprocessTextForWrapping = (text) => {
  if (!text) return text;
  // Prima rimuovi i punti virtuali per la visualizzazione schematica
  let processedText = stripVirtualDots(String(text).trim());

  const separatorPatterns = [
    { regex: /^(.+?)\s+(▲|▼)$/, replacement: '$1 ($2)' },
    { regex: /^(.+?)-(.+)$/, replacement: '$1 ($2)' },
    { regex: /^(.+?)_(.+)$/, replacement: '$1 ($2)' },
  ];

  for (const pattern of separatorPatterns) {
    if (pattern.regex.test(processedText)) {
      processedText = processedText.replace(pattern.regex, pattern.replacement);
      break;
    }
  }
  return processedText.replace(/\s+/g, ' ').trim();
};

// ==================== FUNZIONI HELPER PER TERMINALI ====================

// La capacità di ospitare più cavi è data da allowsMultiple in TERMINAL_DATA, non dal
// prefisso del codice: vale per tutti i terminali, non solo per puntalini PS/PD.
// Un terminale sconosciuto è trattato come singolo, che è l'ipotesi prudente.
export const isSingleTerminal = (terminal, terminali = TERMINAL_DATA) => {
  if (!terminal) return false;
  const config = (terminali || TERMINAL_DATA)[terminal] || {};
  return config.allowsMultiple !== true;
};

export const areTerminalsCompatible = (terminalA, terminalB) => {
  if (terminalA !== terminalB) return false;

  // I terminali SPx non sono mai compatibili tra loro
  const bothAreSpellatura = (
    terminalA && terminalA.startsWith('SP') && terminalA !== 'SP' &&
    terminalB && terminalB.startsWith('SP') && terminalB !== 'SP'
  );

  return !bothAreSpellatura;
};

// ==================== FUNZIONI DISPLAY NAME ====================

export const getEarthCableDisplayName = (cable, language) => {
  if (isStandardCable(cable)) {
    const displaySiglatura = getStandardCableReadableSiglatura(cable);
    if (displaySiglatura) {
      return displaySiglatura;
    }
  }

  // Se è un cavo di terra senza siglatura
  const isEarthWire = normalizeColorToCanonical(cable.colore) === 'YE/GN';
  if (isEarthWire) {
    return getCableLabelTranslation(language, 'earthWire');
  }

  return getCableLabelTranslation(language, 'noMarking');
};

/**
 * Nomi dei cavi multipolari contenuti in un gruppo, ordinati per numerazione e
 * senza duplicati. Un gruppo può contenerne più di uno (multipolari che
 * condividono un morsetto), quindi il titolo deve elencarli tutti.
 */
export const getMultipolarCablesDisplayNames = (cables = [], language) => {
  const fallbackLabel = translations[language]?.cableForm?.multipolarCable
    || translations.it.cableForm.multipolarCable;

  const names = (cables || [])
    .filter(cable => cable && cable.poli && Array.isArray(cable.poli))
    .slice()
    .sort((a, b) => (a.originalIndex ?? 0) - (b.originalIndex ?? 0))
    .map(cable => cable.nomeCavo && cable.nomeCavo.trim() !== ''
      ? cable.nomeCavo
      : `${fallbackLabel} #${(cable.originalIndex ?? 0) + 1}`);

  return [...new Set(names)];
};

export const getCableDisplayName = (cable, language, settings) => {
  settings = withEW3035Settings(settings);
  if (cable.poli && Array.isArray(cable.poli)) {
    if (cable.nomeCavo && cable.nomeCavo.trim() !== '') {
      return cable.nomeCavo;
    } else {
      const text = translations[language]?.cableForm?.multipolarCable;
      return text;
    }
  }

  if (settings.marcaturePerCollegamento) {
    // Modalità collegamento - mostra i collegamenti
    const connA = cable.collegamentoA || '';
    const connB = cable.collegamentoB || '';

    if (connA && connB) {
      return `${connA} → ${connB}`;
    } else if (connA) {
      return `${connA} → —`;
    } else if (connB) {
      return `— → ${connB}`;
    } else {
      return getCableLabelTranslation(language, 'noConnections');
    }
  } else {
    // Modalità normale - usa la logica esistente
    return getEarthCableDisplayName(cable, language);
  }
};

export const getDisplayTitle = (displayKey, sortedCables, isGrouppoForzato = false, nomeGruppo = null, settings = {}) => {
  settings = withEW3035Settings(settings);
  const language = settings?.lingua || 'it';
  const t = (key, params = {}) => {
    const keys = key.split('.');
    let text = translations[language];
    for (const k of keys) {
      if (text && typeof text === 'object' && k in text) {
        text = text[k];
      } else {
        return key;
      }
    }
    if (typeof text === 'string' && params) {
      return text.replace(/\{(\w+)\}/g, (match, key) => params[key] || match);
    }
    return text || key;
  };

  if (isGrouppoForzato && nomeGruppo) {
    const customTitle = getGroupTitle(nomeGruppo);
    if (customTitle && customTitle.trim() !== '') {
      return customTitle;
    }

    // Se tutti i cavi hanno la stessa siglatura, usa la logica normale
    const siglature = [...new Set(sortedCables.map(cable =>
      settings.marcaturePerCollegamento
        ? getCableDisplayName(cable, settings.lingua, settings)
        : getStandardCableDisplaySiglatura(cable)
    ).filter(sig => sig != null && String(sig).trim() !== ''))];

    if (siglature.length === 1) {
      // Tutti hanno la stessa siglatura, usa la logica normale
      if (!settings.marcaturePerCollegamento) {
        return `${t('cableList.cableLabel')} ${siglature[0]}`;
      } else {
        if (sortedCables.length === 1) {
          return `${t('cableList.cableLabel')} #${sortedCables[0].originalIndex + 1}`;
        } else {
          const numbers = sortedCables.map(c => `#${c.originalIndex + 1}`).join(', ');
          return `${t('cableList.cablesLabel')} ${numbers}`;
        }
      }
    }

    return `${nomeGruppo}`;
  }

  const firstCable = sortedCables[0];
  if (firstCable.poli && Array.isArray(firstCable.poli)) {
    // Per cavi multipolari, usa i nomeCavo di tutti i multipolari del gruppo
    const displayNames = getMultipolarCablesDisplayNames(sortedCables, language);
    const label = displayNames.length > 1 ? t('cableList.cablesLabel') : t('cableList.cableLabel');
    return `${label} ${displayNames.join(', ')}`;
  }

  // Logica originale per casi non-gruppo
  if (!settings.marcaturePerCollegamento) {
    const siglatura = getStandardCableReadableSiglatura(sortedCables[0]);
    const siglaturaStr = String(siglatura || '');
    return `${t('cableList.cableLabel')} ${siglaturaStr}`;
  } else {
    // Modalità collegamento - usa numerazione
    if (sortedCables.length === 1) {
      return `${t('cableList.cableLabel')} #${sortedCables[0].originalIndex + 1}`;
    } else {
      const numbers = sortedCables.map(c => `#${c.originalIndex + 1}`).join(', ');
      return `${t('cableList.cablesLabel')} ${numbers}`;
    }
  }
};

// ==================== FUNZIONI DIMENSIONI CAVI ====================

// Il valore della sezione è sempre in mm²: usato direttamente per il
// dimensionamento grafico (larghezza/spessore/altezza preview).
export const getEffectiveSectionForDisplay = (sectionValue) => {
  return getEffectiveSectionValue(sectionValue);
};

export const getCableWidthForTessera = (sectionValue, useAWG = false) => {
  const effectiveSection = getEffectiveSectionForDisplay(sectionValue, useAWG);
  return `${2 + effectiveSection * 3}px`;
};

export const getCableHeightForPreview = (sectionValue, useAWG = false, maxHeight = 24, availableSections = SEZIONI) => {
  const effectiveSection = getEffectiveSectionForDisplay(sectionValue, useAWG);

  const maxSection = Math.max(...availableSections.map(s =>
    getEffectiveSectionForDisplay(s.val, useAWG)
  ));

  const minHeight = 4;
  const height = Math.max(minHeight, (effectiveSection / maxSection) * maxHeight);
  return `${height}px`;
};

export const getStrokeWidth = (sectionValue, isHovered = false, useAWG = false) => {
  const effectiveSection = getEffectiveSectionForDisplay(sectionValue, useAWG);
  const base = 2 + 1.5 * (parseFloat(effectiveSection) || 0.5);
  return isHovered ? base + 1 : base;
};

export const getHoverOverlayMetrics = (sectionValue, useAWG = false) => {
  const strokeWidth = getStrokeWidth(sectionValue, true, useAWG);
  const dashLength = Math.max(4, Math.round(strokeWidth * 0.9));
  const gapLength = Math.max(6, Math.round(strokeWidth * 1.35));
  const dashTravel = -(dashLength + gapLength) * 2;
  const animationDuration = Math.min(1.4, Math.max(0.8, 1 + (strokeWidth - 4) * 0.03));

  return {
    strokeWidth,
    dashArray: `${dashLength} ${gapLength}`,
    dashTravel,
    animationDuration: `${animationDuration}s`
  };
};

// ==================== FUNZIONE PRINCIPALE DI ANALISI ====================

const isMultipolarCable = (cable) => Boolean(cable?.poli && Array.isArray(cable.poli));

const normalizeConnectionValue = (value) => String(value || '').trim();

export const getCableConnectionEndpoints = (cable) => {
  if (!isMultipolarCable(cable)) {
    const { siglaturaA, siglaturaB } = getStandardCableSideSiglature(cable);
    return [
      {
        cable,
        side: 'A',
        connection: normalizeConnectionValue(cable.collegamentoA),
        terminal: cable.terminaleA,
        sezione: cable.sezione,
        siglatura: siglaturaA
      },
      {
        cable,
        side: 'B',
        connection: normalizeConnectionValue(cable.collegamentoB),
        terminal: cable.terminaleB,
        sezione: cable.sezione,
        siglatura: siglaturaB
      }
    ].filter(endpoint => endpoint.connection);
  }

  return cable.poli.flatMap((polo, poloIndex) => {
    const normalizedPolo = normalizeMultipolarPole(polo);

    return [
      {
        cable,
        polo: normalizedPolo,
        poloIndex,
        side: 'A',
        connection: normalizeConnectionValue(normalizedPolo.collegamentoA),
        terminal: normalizedPolo.terminaleA,
        sezione: normalizedPolo.sezione,
        siglatura: normalizedPolo.siglaturaA
      },
      {
        cable,
        polo: normalizedPolo,
        poloIndex,
        side: 'B',
        connection: normalizeConnectionValue(normalizedPolo.collegamentoB),
        terminal: normalizedPolo.terminaleB,
        sezione: normalizedPolo.sezione,
        siglatura: normalizedPolo.siglaturaB
      }
    ];
  }).filter(endpoint => endpoint.connection);
};

const endpointsHaveCompatibleSignature = (endpointA, endpointB, settings) => {
  if (settings.marcaturePerCollegamento) return true;

  const signatureA = String(endpointA.siglatura || '').trim();
  const signatureB = String(endpointB.siglatura || '').trim();

  return signatureA === signatureB;
};

const canEndpointShareTerminal = (endpointA, endpointB, settings = {}) => {
  if (!areTerminalsCompatible(endpointA.terminal, endpointB.terminal)) {
    return false;
  }

  const bothAreSingle =
    isSingleTerminal(endpointA.terminal, settings.terminal_data) &&
    isSingleTerminal(endpointB.terminal, settings.terminal_data);

  if (!bothAreSingle) return true;

  return canTerminalHandleMultipleSingles(endpointA.sezione, endpointA.terminal, settings.terminal_data) ||
    canTerminalHandleMultipleSingles(endpointB.sezione, endpointB.terminal, settings.terminal_data);
};

const areStandardEndpointCablesAdjacent = (endpointA, endpointB, settings) => {
  const cableAIsMultipolar = isMultipolarCable(endpointA.cable);
  const cableBIsMultipolar = isMultipolarCable(endpointB.cable);

  if (cableAIsMultipolar || cableBIsMultipolar) {
    return true;
  }

  if (
    !settings.marcaturePerCollegamento &&
    getStandardCableDisplaySiglatura(endpointA.cable) !== getStandardCableDisplaySiglatura(endpointB.cable)
  ) {
    return false;
  }

  return Math.abs((endpointA.cable.originalIndex ?? 0) - (endpointB.cable.originalIndex ?? 0)) === 1;
};

export const endpointsCanConnect = (endpointA, endpointB, settings) => {
  if (!endpointA.connection || endpointA.connection !== endpointB.connection) {
    return false;
  }

  return canEndpointShareTerminal(endpointA, endpointB, settings) &&
    endpointsHaveCompatibleSignature(endpointA, endpointB, settings);
};

const endpointsCanShareTerminalGroup = (endpointA, endpointB, settings) => {
  if (!endpointsCanConnect(endpointA, endpointB, settings)) {
    return false;
  }

  return areStandardEndpointCablesAdjacent(endpointA, endpointB, settings);
};

const endpointMatchesReference = (endpoint, reference) => {
  if (!endpoint || !reference) return false;

  const endpointCableIndex = endpoint.cable?.originalIndex;
  const referenceCableIndex = reference.cable?.originalIndex;

  return (
    endpoint.side === reference.side &&
    (endpoint.poloIndex ?? null) === (reference.poloIndex ?? null) &&
    endpoint.connection === normalizeConnectionValue(reference.connection) &&
    endpoint.terminal === reference.terminal &&
    (
      endpoint.cable === reference.cable ||
      (
        endpointCableIndex !== undefined &&
        referenceCableIndex !== undefined &&
        endpointCableIndex === referenceCableIndex
      )
    )
  );
};

/**
 * Chiave stabile di un endpoint (lato di un cavo standard o polo+lato di un
 * multipolare). Unico formato usato per identificare un endpoint fra componenti.
 */
export const getEndpointKey = (endpoint) => [
  endpoint?.cable?.originalIndex ?? 'no-index',
  endpoint?.side,
  endpoint?.poloIndex ?? 'no-polo',
  endpoint?.connection,
  endpoint?.terminal
].join('|');

export const getSharedTerminalGroupEndpoints = ({ targetEndpoint, cables = [], settings = {} }) => {
  settings = withEW3035Settings(settings);

  if (!targetEndpoint?.connection || !Array.isArray(cables) || cables.length === 0) {
    return [];
  }

  const normalizedConnection = normalizeConnectionValue(targetEndpoint.connection);
  const candidateEndpoints = cables
    .flatMap(candidate => getCableConnectionEndpoints(candidate))
    .filter(endpoint => endpoint.connection === normalizedConnection);

  const sourceEndpoint = candidateEndpoints.find(endpoint => endpointMatchesReference(endpoint, targetEndpoint));
  if (!sourceEndpoint) {
    return [];
  }

  const visitedKeys = new Set();
  const groupedEndpoints = [];
  const queue = [sourceEndpoint];

  while (queue.length > 0) {
    const currentEndpoint = queue.shift();
    const endpointKey = getEndpointKey(currentEndpoint);

    if (visitedKeys.has(endpointKey)) {
      continue;
    }

    visitedKeys.add(endpointKey);
    groupedEndpoints.push(currentEndpoint);

    candidateEndpoints.forEach((candidateEndpoint) => {
      if (visitedKeys.has(getEndpointKey(candidateEndpoint))) {
        return;
      }

      if (endpointsCanShareTerminalGroup(currentEndpoint, candidateEndpoint, settings)) {
        queue.push(candidateEndpoint);
      }
    });
  }

  return groupedEndpoints;
};

/**
 * Gruppi di morsetto condiviso presenti in un insieme di cavi: ogni gruppo raccoglie
 * gli endpoint (lati di cavi standard e poli di multipolari) che arrivano allo stesso
 * punto di collegamento. Vengono restituiti solo i gruppi con almeno due endpoint,
 * numerati nell'ordine in cui compaiono nei cavi passati.
 *
 * @returns {{ groups: Array, byEndpointKey: Map<string, number> }} i gruppi e la
 *   mappa chiave-endpoint → indice del gruppo, per interrogarla dal rendering.
 */
export const getTerminalGroupsForCables = (cables = [], settings = {}) => {
  const emptyResult = { groups: [], byEndpointKey: new Map() };
  if (!Array.isArray(cables) || cables.length === 0) return emptyResult;

  settings = withEW3035Settings(settings);

  const assignedKeys = new Set();
  const groups = [];

  cables.forEach(cable => {
    getCableConnectionEndpoints(cable).forEach(endpoint => {
      if (assignedKeys.has(getEndpointKey(endpoint))) return;

      const groupEndpoints = getSharedTerminalGroupEndpoints({
        targetEndpoint: endpoint,
        cables,
        settings
      });

      groupEndpoints.forEach(groupEndpoint => assignedKeys.add(getEndpointKey(groupEndpoint)));

      if (groupEndpoints.length > 1) {
        const terminalConfig = TERMINAL_DATA[endpoint.terminal] || {};
        const maxSezione = terminalConfig.maxSection ?? null;
        const sezioneTotale = Math.round(
          groupEndpoints.reduce((sum, e) => sum + normalizeSectionForComparison(e.sezione), 0) * 100
        ) / 100;

        groups.push({
          connection: endpoint.connection,
          terminal: endpoint.terminal,
          endpoints: groupEndpoints,
          // Sezioni in mm²: la conversione in AWG è solo di visualizzazione.
          sezioneTotale,
          maxSezione,
          isOverCapacity: maxSezione != null && sezioneTotale > maxSezione
        });
      }
    });
  });

  const byEndpointKey = new Map();
  groups.forEach((group, groupIndex) => {
    group.endpoints.forEach(endpoint => byEndpointKey.set(getEndpointKey(endpoint), groupIndex));
  });

  return { groups, byEndpointKey };
};

/**
 * Gruppo di morsetto a cui appartiene un endpoint, o null se non ne condivide uno.
 * La costruzione della chiave resta qui: chi renderizza passa cavo, lato ed eventuale
 * polo, senza dover conoscere il formato.
 */
export const getTerminalGroupForEndpoint = (terminalGroups, cable, side, poloIndex = null) => {
  if (!terminalGroups?.byEndpointKey?.size || !cable) return null;

  const endpoint = getCableConnectionEndpoints(cable).find(candidate =>
    candidate.side === side && (candidate.poloIndex ?? null) === (poloIndex ?? null)
  );
  if (!endpoint) return null;

  const groupIndex = terminalGroups.byEndpointKey.get(getEndpointKey(endpoint));
  if (groupIndex === undefined) return null;

  return { groupIndex, group: terminalGroups.groups[groupIndex] };
};

const cablesCanConnect = (cableA, cableB, settings) => {
  const endpointsA = getCableConnectionEndpoints(cableA);
  const endpointsB = getCableConnectionEndpoints(cableB);

  const hasMatchingEndpoint = endpointsA.some(endpointA =>
    endpointsB.some(endpointB => endpointsCanConnect(endpointA, endpointB, settings))
  );

  if (!hasMatchingEndpoint) return false;

  const areAdjacent = Math.abs((cableA.originalIndex ?? 0) - (cableB.originalIndex ?? 0)) === 1;

  if (!isMultipolarCable(cableA) && !isMultipolarCable(cableB)) {
    if (!settings.marcaturePerCollegamento && getStandardCableDisplaySiglatura(cableA) !== getStandardCableDisplaySiglatura(cableB)) {
      return false;
    }
    return areAdjacent;
  }

  // Due multipolari si uniscono solo se numericamente adiacenti, come i cavi standard:
  // un gruppo che salta delle posizioni trascinerebbe il cavo più lontano sotto al
  // primo del gruppo, rompendo l'ordine di numerazione dell'elenco.
  if (isMultipolarCable(cableA) && isMultipolarCable(cableB)) {
    return areAdjacent;
  }

  return true;
};

export const getMultipolarPoleConnections = (multipolarCable, candidateCables = [], settings = {}) => {
  settings = withEW3035Settings(settings);
  if (!isMultipolarCable(multipolarCable)) return [];

  const multipolarEndpoints = getCableConnectionEndpoints(multipolarCable);
  const candidateList = candidateCables.filter(candidate =>
    candidate &&
    candidate.originalIndex !== multipolarCable.originalIndex &&
    !isMultipolarCable(candidate)
  );

  return multipolarCable.poli.map((polo, poloIndex) => {
    const getConnectionsForSide = (side) => {
      const poleEndpoint = multipolarEndpoints.find(endpoint =>
        endpoint.poloIndex === poloIndex && endpoint.side === side
      );

      if (!poleEndpoint) return [];

      const directlyConnected = candidateList.filter(candidate =>
        getCableConnectionEndpoints(candidate).some(candidateEndpoint =>
          endpointsCanConnect(poleEndpoint, candidateEndpoint, settings)
        )
      );

      if (directlyConnected.length === 0) return [];

      const connectedIndexes = new Set(directlyConnected.map(candidate => candidate.originalIndex));
      const toProcess = [...directlyConnected];

      while (toProcess.length > 0) {
        const currentCable = toProcess.shift();

        candidateList.forEach(candidate => {
          if (connectedIndexes.has(candidate.originalIndex)) return;

          if (cablesCanConnect(currentCable, candidate, settings)) {
            connectedIndexes.add(candidate.originalIndex);
            toProcess.push(candidate);
          }
        });
      }

      return candidateList.filter(candidate =>
        connectedIndexes.has(candidate.originalIndex)
      );
    };

    return {
      polo,
      poloIndex,
      connectionsA: getConnectionsForSide('A'),
      connectionsB: getConnectionsForSide('B')
    };
  });
};

export const analyzeAndGroupCables = (cablesArr, settings) => {
  settings = withEW3035Settings(settings);
  const subGroups = [];
  const processed = new Set();

  const areTerminalsCompatible = (terminalA, terminalB) => {
    if (terminalA !== terminalB) return false;

    // I terminali SPx non sono mai compatibili tra loro
    const bothAreSpellatura = (
      terminalA && terminalA.startsWith('SP') && terminalA !== 'SP' &&
      terminalB && terminalB.startsWith('SP') && terminalB !== 'SP'
    );

    return !bothAreSpellatura;
  };

  cablesArr.forEach((cable, idx) => {
    if (processed.has(idx)) return;

    const hasSingleTerminalA = isSingleTerminal(cable.terminaleA);
    const hasSingleTerminalB = isSingleTerminal(cable.terminaleB);

    const shouldBeIsolated = !cablesArr.some((other, otherIdx) => {
      if (otherIdx === idx) return false;

      const shareCollegamentoA = cable.collegamentoA &&
        (cable.collegamentoA === other.collegamentoA || cable.collegamentoA === other.collegamentoB);
      const shareCollegamentoB = cable.collegamentoB &&
        (cable.collegamentoB === other.collegamentoA || cable.collegamentoB === other.collegamentoB);

      let hasCompatibleTerminals = false;

      if (shareCollegamentoA) {
        if (cable.collegamentoA === other.collegamentoA) {
          hasCompatibleTerminals = areTerminalsCompatible(cable.terminaleA, other.terminaleA);
          if (settings.marcaturePerCollegamento) {
            hasCompatibleTerminals = hasCompatibleTerminals && cable.terminaleA === other.terminaleA;
          }
        } else if (cable.collegamentoA === other.collegamentoB) {
          hasCompatibleTerminals = areTerminalsCompatible(cable.terminaleA, other.terminaleB);
          if (settings.marcaturePerCollegamento) {
            hasCompatibleTerminals = hasCompatibleTerminals && cable.terminaleA === other.terminaleB;
          }
        }
      }

      if (shareCollegamentoB && !hasCompatibleTerminals) {
        if (cable.collegamentoB === other.collegamentoA) {
          hasCompatibleTerminals = areTerminalsCompatible(cable.terminaleB, other.terminaleA);
          if (settings.marcaturePerCollegamento) {
            hasCompatibleTerminals = hasCompatibleTerminals && cable.terminaleB === other.terminaleA;
          }
        } else if (cable.collegamentoB === other.collegamentoB) {
          hasCompatibleTerminals = areTerminalsCompatible(cable.terminaleB, other.terminaleB);
          if (settings.marcaturePerCollegamento) {
            hasCompatibleTerminals = hasCompatibleTerminals && cable.terminaleB === other.terminaleB;
          }
        }
      }

      const canSingleTerminalHandleMultiple = (cable, terminalSide) => {
        const terminal = terminalSide === 'A' ? cable.terminaleA : cable.terminaleB;
        return canTerminalHandleMultipleSingles(cable.sezione, terminal);
      };

      if (shareCollegamentoA && hasSingleTerminalA && !hasCompatibleTerminals) {
        const canHandleMultiple = canSingleTerminalHandleMultiple(cable, 'A') ||
          canSingleTerminalHandleMultiple(other, 'A');
        if (!canHandleMultiple) {
          return false;
        }
      }
      if (shareCollegamentoB && hasSingleTerminalB && !hasCompatibleTerminals) {
        const canHandleMultiple = canSingleTerminalHandleMultiple(cable, 'B') ||
          canSingleTerminalHandleMultiple(other, 'B');
        if (!canHandleMultiple) {
          return false;
        }
      }

      return (shareCollegamentoA || shareCollegamentoB) && hasCompatibleTerminals;
    });

    const hasEndpointConnections = cablesArr.some((other, otherIdx) =>
      otherIdx !== idx && cablesCanConnect(cable, other, settings)
    );

    if (shouldBeIsolated && !hasEndpointConnections) {
      processed.add(idx);
      subGroups.push({
        cables: [cable],
        isSingle: true
      });
      return;
    }

    const group = [cable];
    const toProcess = [cable];
    processed.add(idx);
    const processedInGroup = new Set([idx]);

    while (toProcess.length > 0) {
      const currentCable = toProcess.shift();

      cablesArr.forEach((otherCable, otherIdx) => {
        if (processed.has(otherIdx) || processedInGroup.has(otherIdx)) return;

        if (cablesCanConnect(currentCable, otherCable, settings)) {
          group.push(otherCable);
          toProcess.push(otherCable);
          processed.add(otherIdx);
          processedInGroup.add(otherIdx);
          return;
        }

        let shouldConnect = false;
        let hasCompatibleTerminals = false;

        if (currentCable.collegamentoA && otherCable.collegamentoA === currentCable.collegamentoA) {
          hasCompatibleTerminals = areTerminalsCompatible(currentCable.terminaleA, otherCable.terminaleA);
          if (settings.marcaturePerCollegamento) {
            hasCompatibleTerminals = hasCompatibleTerminals && currentCable.terminaleA === otherCable.terminaleA;
          }
          shouldConnect = hasCompatibleTerminals && !(isSingleTerminal(currentCable.terminaleA) && isSingleTerminal(otherCable.terminaleA));
        } else if (currentCable.collegamentoA && otherCable.collegamentoB === currentCable.collegamentoA) {
          hasCompatibleTerminals = areTerminalsCompatible(currentCable.terminaleA, otherCable.terminaleB);
          if (settings.marcaturePerCollegamento) {
            hasCompatibleTerminals = hasCompatibleTerminals && currentCable.terminaleA === otherCable.terminaleB;
          }
          shouldConnect = hasCompatibleTerminals && !(isSingleTerminal(currentCable.terminaleA) && isSingleTerminal(otherCable.terminaleB));
        }

        if (!shouldConnect && currentCable.collegamentoB && otherCable.collegamentoA === currentCable.collegamentoB) {
          hasCompatibleTerminals = areTerminalsCompatible(currentCable.terminaleB, otherCable.terminaleA);
          if (settings.marcaturePerCollegamento) {
            hasCompatibleTerminals = hasCompatibleTerminals && currentCable.terminaleB === otherCable.terminaleA;
          }
          shouldConnect = hasCompatibleTerminals && !(isSingleTerminal(currentCable.terminaleB) && isSingleTerminal(otherCable.terminaleA));
        } else if (!shouldConnect && currentCable.collegamentoB && otherCable.collegamentoB === currentCable.collegamentoB) {
          hasCompatibleTerminals = areTerminalsCompatible(currentCable.terminaleB, otherCable.terminaleB);
          if (settings.marcaturePerCollegamento) {
            hasCompatibleTerminals = hasCompatibleTerminals && currentCable.terminaleB === otherCable.terminaleB;
          }
          shouldConnect = hasCompatibleTerminals && !(isSingleTerminal(currentCable.terminaleB) && isSingleTerminal(otherCable.terminaleB));
        }

        if (shouldConnect) {
          // Controllo generale di consecutività - i cavi devono essere consecutivi per essere raggruppati
          const indexDifference = Math.abs(currentCable.originalIndex - otherCable.originalIndex);
          if (indexDifference !== 1) {
            shouldConnect = false;
          }

          if (shouldConnect) {
            // In modalità collegamento, la siglatura non è un fattore di raggruppamento
            const hasDifferentSignature = settings.marcaturePerCollegamento
              ? false
              : (getStandardCableDisplaySiglatura(currentCable) !== getStandardCableDisplaySiglatura(otherCable));

            const isSingleButCanHandleMultiple = (cable, terminal) => {
              return canTerminalHandleMultipleSingles(cable.sezione, terminal);
            };

            const otherHasSingleOnSharedConnection =
              (otherCable.collegamentoA === currentCable.collegamentoA && isSingleTerminal(otherCable.terminaleA) && !isSingleButCanHandleMultiple(otherCable, otherCable.terminaleA)) ||
              (otherCable.collegamentoA === currentCable.collegamentoB && isSingleTerminal(otherCable.terminaleA) && !isSingleButCanHandleMultiple(otherCable, otherCable.terminaleA)) ||
              (otherCable.collegamentoB === currentCable.collegamentoA && isSingleTerminal(otherCable.terminaleB) && !isSingleButCanHandleMultiple(otherCable, otherCable.terminaleB)) ||
              (otherCable.collegamentoB === currentCable.collegamentoB && isSingleTerminal(otherCable.terminaleB) && !isSingleButCanHandleMultiple(otherCable, otherCable.terminaleB));

            if (!otherHasSingleOnSharedConnection && !hasDifferentSignature) {
              group.push(otherCable);
              toProcess.push(otherCable);
              processed.add(otherIdx);
              processedInGroup.add(otherIdx);
            }
          }
        }
      });
    }

    if (group.length === 1) {
      subGroups.push({
        cables: group,
        isSingle: true
      });
    } else {
      const hasMultipolarMember = group.some(isMultipolarCable);
      const orderedGroup = hasMultipolarMember
        ? [...group].sort((a, b) => (a.originalIndex ?? 0) - (b.originalIndex ?? 0))
        : group;
      const finalGroups = hasMultipolarMember
        ? [orderedGroup]
        : splitGroupByTerminalTypes(orderedGroup);
      finalGroups.forEach((finalGroup) => {
        subGroups.push({
          cables: finalGroup,
          isSingle: finalGroup.length === 1
        });
      });
    }
  });

  subGroups.forEach(group => {
    group.cables.forEach(cable => {
      cable.type = group.isSingle ? 'single' : 'multiple';
      cable.groupSize = group.cables.length;
    });
  });

  return subGroups;
};

// Funzione helper per dividere gruppi basandosi sui tipi di terminali
const splitGroupByTerminalTypes = (group) => {
  if (group.length <= 1) return [group];

  const connectionMap = {};

  group.forEach(cable => {
    if (cable.collegamentoA) {
      if (!connectionMap[cable.collegamentoA]) {
        connectionMap[cable.collegamentoA] = [];
      }
      connectionMap[cable.collegamentoA].push({
        cable,
        terminal: cable.terminaleA,
        side: 'A'
      });
    }
    if (cable.collegamentoB) {
      if (!connectionMap[cable.collegamentoB]) {
        connectionMap[cable.collegamentoB] = [];
      }
      connectionMap[cable.collegamentoB].push({
        cable,
        terminal: cable.terminaleB,
        side: 'B'
      });
    }
  });

  const singleOnlyConnections = new Set();
  Object.entries(connectionMap).forEach(([conn, items]) => {
    if (items.length > 1 && items.every(item => {
      // MODIFICA: Solo se è terminale singolo E non può gestire multipli
      return isSingleTerminal(item.terminal) &&
        !canTerminalHandleMultipleSingles(item.cable.sezione, item.terminal);
    })) {
      singleOnlyConnections.add(conn);
    }
  });

  if (singleOnlyConnections.size === 0) {
    return [group];
  }

  const separatedCables = [];
  const remainingCables = [];

  group.forEach(cable => {
    const shouldSeparate =
      (singleOnlyConnections.has(cable.collegamentoA) && isSingleTerminal(cable.terminaleA)) ||
      (singleOnlyConnections.has(cable.collegamentoB) && isSingleTerminal(cable.terminaleB));

    if (shouldSeparate) {
      separatedCables.push(cable);
    } else {
      remainingCables.push(cable);
    }
  });

  const result = [];
  separatedCables.forEach(cable => {
    result.push([cable]);
  });

  if (remainingCables.length > 0) {
    result.push(remainingCables);
  }

  return result;
};

export const getGroupedCablesAsArray = (groupedCablesObject, cables, listSortMode, settings) => {
  settings = withEW3035Settings(settings);
  if (!groupedCablesObject || Object.keys(groupedCablesObject).length === 0) {
    return [];
  }

  const groupsArray = Object.entries(groupedCablesObject).map(([key, value]) => {
    const cablesInGroup = Array.isArray(value) ? value : value.allCables;
    return {
      key: key,
      groupData: value,
      cables: cablesInGroup,
      isForcedGroup: !Array.isArray(value),
      firstCable: cablesInGroup[0],
    };
  });

  return groupsArray.sort((a, b) => {
    const getFirstIndex = (groupData) => {
      if (Array.isArray(groupData.cables)) {
        // Gruppo normale - array di cavi
        return Math.min(...groupData.cables.map(c => c.originalIndex));
      } else if (groupData.groupData && groupData.groupData.type === 'forced_group') {
        // Gruppo forzato - usa allCables
        return Math.min(...groupData.groupData.allCables.map(c => c.originalIndex));
      } else {
        // Fallback
        return 0;
      }
    };

    const firstIndexA = getFirstIndex(a);
    const firstIndexB = getFirstIndex(b);

    if (listSortMode === 'numerazione') {
      return firstIndexA - firstIndexB;
    }

    // Per altri ordinamenti, usa il sistema unificato
    const context = createSortContext(settings.lingua, settings.useAWG);

    const getCablesArray = (groupData) => {
      if (Array.isArray(groupData.cables)) {
        return groupData.cables;
      } else if (groupData.groupData && groupData.groupData.type === 'forced_group') {
        return groupData.groupData.allCables;
      } else {
        return [];
      }
    };

    const groupA = { cables: getCablesArray(a) };
    const groupB = { cables: getCablesArray(b) };

    const [sortedA] = universalSort([groupA, groupB], listSortMode, context);
    return sortedA === groupA ? -1 : 1;
  });
};

// Ottiene le siglature da stampare per un cavo in modalità collegamento
export const getSiglaturaPerStampa = (cable, settings) => {
  settings = withEW3035Settings(settings);
  if (!settings.marcaturePerCollegamento) {
    const { siglaturaA, siglaturaB } = getStandardCableSideSiglature(cable);
    const siglature = [];
    if (siglaturaA) siglature.push(siglaturaA);
    if (siglaturaB && siglaturaB !== siglaturaA) siglature.push(siglaturaB);
    if (siglaturaB && siglaturaB === siglaturaA) siglature.push(siglaturaB);
    return siglature;
  }

  // Modalità collegamento - estrai da entrambi i collegamenti
  const sigA = extractSiglaturaFromCollegamento(cable.collegamentoA);
  const sigB = extractSiglaturaFromCollegamento(cable.collegamentoB);

  // Ritorna array delle siglature uniche non vuote
  const siglature = [];
  if (sigA && sigA.trim() !== '') siglature.push(sigA);
  if (sigB && sigB.trim() !== '' && sigB !== sigA) siglature.push(sigB);

  return siglature;
};

export const extractSiglaturaFromCollegamento = (collegamento) => {
  if (!collegamento) return '';
  const match = collegamento.match(/^([^(]+)/);
  return match ? match[1].trim() : collegamento;
};

export const estimateTextWidth = (text, fontSize = 14, charWidth = 7) => {
  return text.length * charWidth;
};

export const replaceArrowsForPDF = (text) => {
  if (!text) return text;
  return String(text)
    .replace(/▲/g, 'UP')
    .replace(/▼/g, 'DW');
};

// Verifica se terminale ha grafica custom
export const hasCustomTerminalGraphic = (terminalCode) => {
  return CUSTOM_TERMINALS.includes(terminalCode);
};

export const getTerminalData = (terminalCode, settings = {}, terminali = null, isMultiple = false) => {
  settings = withEW3035Settings(settings);
  if (!terminalCode) return { len: 0, peel: 0, nome: '' };

  const language = settings?.lingua || 'it';
  const t = (key, params = {}) => {
    const keys = key.split('.');
    let text = translations[language];
    for (const k of keys) {
      if (text && typeof text === 'object' && k in text) {
        text = text[k];
      } else {
        return key;
      }
    }
    if (typeof text === 'string' && params) {
      return text.replace(/\{(\w+)\}/g, (match, key) => params[key] || match);
    }
    return text || key;
  };

  // Nuovo formato SPx/y dove x=len e y=peel
  if (terminalCode.startsWith('SP') && terminalCode.length > 2) {
    const match = terminalCode.match(/^SP(\d+)\/(\d+)$/);
    if (match) {
      const length = parseInt(match[1]) || 0;
      const peel = parseInt(match[2]) || 0;
      const printOffset = length;
      return {
        len: length,
        peel: peel,
        printOffset: printOffset,
        nome: t('terminals.customStripping', { length, peel })
      };
    }

    // Fallback per formato vecchio SPx (compatibilità)
    const length = parseInt(terminalCode.substring(2)) || 0;
    return {
      len: length,
      peel: 3,
      printOffset: length,
      nome: t('terminals.customStrippingOld', { length })
    };
  }

  // Usa terminali personalizzati se disponibili, altrimenti usa i valori hardcoded
  const terminalData = terminali || TERMINAL_DATA;
  const rawData = terminalData[terminalCode] || { len: 0, peel: 0, nome: '' };

  const baseData = { ...rawData };
  if (isMultiple) {
    baseData.len = baseData.multiLen !== undefined ? baseData.multiLen : baseData.len;
    baseData.peel = baseData.multiPeel !== undefined ? baseData.multiPeel : baseData.peel;
    baseData.printOffset = baseData.multiPrintOffset !== undefined ? baseData.multiPrintOffset : baseData.printOffset;
  }

  // Traduci il nome del terminale
  const terminalKey = terminalCode.replace(/\./g, '_');
  const translatedName = t(`terminals.${terminalKey}`);

  return {
    ...baseData,
    nome: translatedName !== `terminals.${terminalKey}` ? translatedName : baseData.nome
  };
};

// Split testo per etichette multi-linea
export const splitText = (text, maxLen = 14) => {
  if (!text) return [''];

  const processedText = preprocessTextForWrapping(text); // Applica la formattazione prima dello split

  const idx = processedText.indexOf(' ');
  if (idx === -1) return [processedText];

  const first = processedText.slice(0, idx);
  const rest = processedText.slice(idx + 1);

  if (rest.length <= maxLen) {
    return [first, rest];
  }

  const words = rest.split(' ');
  const lines = [];
  let current = '';

  words.forEach(word => {
    if ((current + ' ' + word).trim().length > maxLen) {
      if (current) lines.push(current.trim());
      current = word;
    } else {
      current += ' ' + word;
    }
  });
  if (current) lines.push(current.trim());

  return [first, ...lines];
};

export const getTerminalGroups = (terminali, language = 'it') => {
  if (!terminali || typeof terminali !== 'object') {
    return {};
  }

  const t = (key) => {
    const keys = key.split('.');
    let text = translations[language];
    if (!text) {
      // Fallback a italiano se la lingua non esiste
      text = translations['it'];
    }
    for (const k of keys) {
      if (text && typeof text === 'object' && k in text) {
        text = text[k];
      } else {
        // Se non trovato, prova con italiano come fallback
        if (language !== 'it') {
          text = translations['it'];
          for (const k2 of keys) {
            if (text && typeof text === 'object' && k2 in text) {
              text = text[k2];
            } else {
              return key;
            }
          }
          return text || key;
        }
        return key;
      }
    }
    return (typeof text === 'string' ? text : key);
  };

  // Costruisci la mappa inversa: TerminalCode -> GroupKey
  const terminalToGroupMap = {};

  // Importante: Importiamo TERMINAL_GROUPS dal constants
  const { TERMINAL_GROUPS } = require('../constants/terminalConstants');

  Object.entries(TERMINAL_GROUPS).forEach(([groupKey, codes]) => {
    codes.forEach(code => {
      terminalToGroupMap[code] = groupKey;
    });
  });

  // 2. Inizializza l'oggetto dei gruppi di output con le chiavi tradotte
  const groups = {};

  // Usa l'ordine delle chiavi definito in TERMINAL_GROUPS come ordine di visualizzazione
  const orderedKeys = Object.keys(TERMINAL_GROUPS);

  orderedKeys.forEach(groupKey => {
    // Traduci la chiave del gruppo
    const finalGroupName = t(groupKey);
    groups[finalGroupName] = [];
  });

  // 3. Itera sui terminali passati e assegnali ai gruppi
  Object.entries(sanitizeTerminalData(terminali)).forEach(([cod, term]) => {
    // Traduci sempre il nome del terminale usando il codice
    const terminalKey = cod.replace(/\./g, '_');
    const translationKey = `terminals.${terminalKey}`;
    const translatedName = t(translationKey);
    const finalName = (translatedName && translatedName !== translationKey) ? translatedName : (term.nome || cod);

    const termWithTranslatedName = {
      ...term,
      nome: finalName
    };

    // Determina il gruppo (chiave)
    // Se non trovato, usa 'terminals.others'
    const groupKey = terminalToGroupMap[cod] || 'terminals.others';

    // Traduci la chiave del gruppo per ottenere il nome visualizzato
    const targetGroupName = t(groupKey);

    // Se il gruppo non esiste (in caso di chiavi non presenti in TERMINAL_GROUPS iniziali), inizializzalo
    if (!groups[targetGroupName]) {
      groups[targetGroupName] = [];
    }

    groups[targetGroupName].push([cod, termWithTranslatedName]);
  });

  // 4. Rimuovi gruppi vuoti
  Object.keys(groups).forEach(key => {
    if (groups[key].length === 0) {
      delete groups[key];
    }
  });

  return groups;
};
